import { useEffect, useState } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import OrchidProAvatar from "components/orchid-pro-avatar";
import AvailabilityTag from "components/orchid-pro/availability-tag/AvailabilityTag";
import {
  getOrchidProCertificationString,
  getOrchidProTypeStringFromNewArr,
  getOrchidProAbbrevNameAndAllDegreesString,
} from "utils/formatter/orchidPro";
import NextButton from "components/pagination-buttons/components/next-button";
import PreviousButton from "components/pagination-buttons/components/previous-button";
import styles from "./styles.module.scss";
import searchOrchidPro from "services/searchOrchidPro";
const MeetOurProfessionals = () => {
  const [{ orchidPros, totalCount }, setSearchOrchidProResult] = useState<{
    orchidPros: OrchidPro[];
    totalCount: number;
  }>({
    orchidPros: [],
    totalCount: 0,
  });

  const { isPhonePortrait } = usePhonePortraitContext();
  const [page, setPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(4);

  const updateDimensions = () => {
    setCountPerPage(window.innerWidth > 1200 ? 3 : 3);
  };

  const getOrchidPro = async (nextPage: number) => {
    try {
      const res = await searchOrchidPro({
        limit: countPerPage,
        skip: (nextPage - 1) * countPerPage,
      });
      setSearchOrchidProResult({
        orchidPros: res.results,
        totalCount: res.total_count,
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getOrchidPro(page);
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
    // on init
    // eslint-disable-next-line
  }, []);

  const nextPage = (page) => {
    const nextPage = page + 1;
    setPage(nextPage);
    getOrchidPro(nextPage);
  };

  const prevPage = (page) => {
    const nextPage = page - 1;
    setPage(nextPage);
    getOrchidPro(nextPage);
  };

  const pageMax = Math.ceil(totalCount / countPerPage);

  return !isPhonePortrait ? (
    <Container className={styles["meet-our-professionals__layout"]}>
      <Row className="d-flex mb-4">
        <Col md={8} className="px-4">
          <h2 className="deep-blue rg-32 questrial">Meet our professionals</h2>
        </Col>
        <Col md={4} className="text-right my-auto">
          <PreviousButton disabled={page <= 1} onClick={() => prevPage(page)} />
          <NextButton
            disabled={page >= pageMax}
            onClick={() => nextPage(page)}
          />
        </Col>
      </Row>
      <Row className={styles["meet-our-professionals"]}>
        {orchidPros.slice(0, countPerPage).map((orchidPro) => {
          return (
            <Col
              md={Math.floor(12 / countPerPage)}
              sm={8}
              className="d-flex px-2"
              key={orchidPro.cognito_sub}
            >
              <Link
                className="d-flex w-100"
                to={"/professional-search/" + orchidPro.custom_url}
              >
                <Card className="w-100 rounded-lg">
                  <Card.Body className={styles["orchid-pro-card-body"]}>
                    <OrchidProAvatar
                      orchidPro={orchidPro}
                      style={{
                        width: "7.5rem",
                        height: "7.5rem",
                      }}
                    />
                    <div className="mt-3 text-center">
                      <div className={"deep-blue rg-24"}>
                        {getOrchidProAbbrevNameAndAllDegreesString(orchidPro)}
                      </div>
                      {!orchidPro.orchid_pro_types?.length ? null : (
                        <p className={"mb-0 rg-16 darker"}>
                          {getOrchidProTypeStringFromNewArr(orchidPro)}
                        </p>
                      )}
                      {!orchidPro.orchid_pro_types?.length ? null : (
                        <p className="rg-14 gray01">
                          {getOrchidProCertificationString(orchidPro)}
                        </p>
                      )}
                    </div>
                  </Card.Body>
                  <Card.Footer className={styles["orchid-pro-card-footer"]}>
                    {orchidPro.can_accept_payment && (
                      <AvailabilityTag
                        orchid_pro_cognito_sub={orchidPro.cognito_sub}
                        highlightToday
                      />
                    )}
                  </Card.Footer>
                </Card>
              </Link>
            </Col>
          );
        })}
      </Row>
    </Container>
  ) : (
    <div className={styles["meet-our-professionals__layout--phone"]}>
      <Row className="d-flex mb-4 px-4 justify-content-between">
        <h5 className="fs-18 mb-0">Meet our professionals</h5>
        <Link to="/professional-search">See all</Link>
      </Row>
      <Row className={styles["meet-our-professionals__list--phone"]}>
        {orchidPros.slice(0, countPerPage).map((orchidPro) => {
          return (
            <Col
              md={Math.floor(12 / countPerPage)}
              sm={8}
              xs={8}
              className="d-flex px-2"
              key={orchidPro.cognito_sub}
            >
              <Link
                className="d-flex w-100"
                to={"/professional-search/" + orchidPro.custom_url}
              >
                <Card className="w-100 rounded-lg">
                  <Card.Body className={styles["orchid-pro-card-body--phone"]}>
                    <OrchidProAvatar
                      orchidPro={orchidPro}
                      style={{
                        width: "3.625rem",
                        height: "3.625rem",
                      }}
                    />
                    <div className="mt-3 text-center">
                      <div className={"deep-blue rg-15"}>
                        {getOrchidProAbbrevNameAndAllDegreesString(orchidPro)}
                      </div>
                      {!orchidPro.orchid_pro_types?.length ? null : (
                        <p
                          className={
                            "mb-0 rg-13-footnote gray01 module line-clamp-1"
                          }
                        >
                          {getOrchidProTypeStringFromNewArr(orchidPro)}
                        </p>
                      )}
                    </div>
                  </Card.Body>
                  <Card.Footer
                    className={styles["orchid-pro-card-footer--phone"]}
                  >
                    {orchidPro.can_accept_payment && (
                      <AvailabilityTag
                        orchid_pro_cognito_sub={orchidPro.cognito_sub}
                        highlightToday
                      />
                    )}
                  </Card.Footer>
                </Card>
              </Link>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default MeetOurProfessionals;
